// Date, time, and duration formatting utilities

const MONTH_NAMES = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

/**
 * Format date as "DD MMM YY" (e.g., "16 JUN 23")
 * Parses ISO string directly to avoid timezone conversion - flight times are always local airport time
 */
export function formatDate(dateString: string): string {
  // Extract date directly from ISO string to avoid timezone conversion
  const [datePart] = dateString.split('T');
  if (datePart && datePart.includes('-')) {
    const [year, month, day] = datePart.split('-').map(Number);
    return `${day} ${MONTH_NAMES[month - 1]} ${year.toString().slice(-2)}`;
  }
  // Fallback for non-ISO formats
  const date = new Date(dateString);
  const day = date.getUTCDate();
  const month = MONTH_NAMES[date.getUTCMonth()];
  const year = date.getUTCFullYear().toString().slice(-2);
  return `${day} ${month} ${year}`;
}

/**
 * Format date as "DD.MM.YYYY" (German format)
 * Parses ISO string directly to avoid timezone conversion
 */
export function formatDateGerman(dateString: string): string {
  // Extract date directly from ISO string to avoid timezone conversion
  const [datePart] = dateString.split('T');
  if (datePart && datePart.includes('-')) {
    const [year, month, day] = datePart.split('-');
    return `${day}.${month}.${year}`;
  }
  // Fallback for non-ISO formats
  const date = new Date(dateString);
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Format time as "HH:MM" (24-hour format)
 * Parses ISO string directly to avoid timezone conversion - flight times are always local airport time
 */
export function formatTime(dateString: string): string {
  // Extract time directly from ISO string to avoid timezone conversion
  const timePart = dateString.split('T')[1];
  if (timePart) {
    return timePart.slice(0, 5); // "14:30"
  }
  // Fallback for non-ISO formats
  const date = new Date(dateString);
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  });
}

/**
 * Format datetime as "DD.MM.YYYY HH:MM"
 */
export function formatDateTime(dateString: string): string {
  return `${formatDateGerman(dateString)} ${formatTime(dateString)}`;
}

/**
 * Format duration in seconds to human-readable string
 * e.g., 7800 -> "2h 10m"
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

/**
 * Format connection time with label
 * e.g., 7800 -> "2h 10m connection"
 */
export function formatConnectionTime(seconds: number): string {
  return `${formatDuration(seconds)} connection`;
}

/**
 * Format money amount with currency symbol
 */
export function formatMoney(value: number, currencyCode: string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return formatter.format(value / 100); // Assuming values are in cents
}

/**
 * Format money amount without dividing (for values already in main unit)
 */
export function formatMoneyRaw(value: number, currencyCode: string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return formatter.format(value);
}

/**
 * Format passenger type code to readable text
 */
export function formatPassengerType(code: string): string {
  const types: Record<string, string> = {
    ADT: 'Adult',
    CHD: 'Child',
    INF: 'Infant',
  };
  return types[code] || code;
}

/**
 * Format gender code to readable text
 */
export function formatGender(gender: string): string {
  const genders: Record<string, string> = {
    male: 'Male',
    female: 'Female',
    M: 'Male',
    F: 'Female',
  };
  return genders[gender] || gender;
}

/**
 * Get traveler full name from traveler object
 */
export function getTravelerName(
  travelers: Array<{ id: string; names: Array<{ firstName: string; lastName: string }> }>,
  travelerId: string
): string {
  const traveler = travelers.find(t => t.id === travelerId);
  if (!traveler || !traveler.names[0]) {
    return 'Unknown Traveler';
  }
  const name = traveler.names[0];
  return `${name.firstName} ${name.lastName}`;
}

/**
 * Format flight number (airline code + number)
 */
export function formatFlightNumber(airlineCode: string, flightNumber: string): string {
  return `${airlineCode}${flightNumber}`;
}

/**
 * Get relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateGerman(dateString);
}
